import React, { useContext } from "react"
import tw from "twin.macro"
import { LeaseFeaturedMobileLinksProps } from "./HeroCarousel.d"
import { LocalInventoryLinks } from "../../../molecules/LocalInventoryLinks"
import { PriceAndMPG } from "../../../molecules/PriceAndMPG"
import { LocationContext } from "../../../../contexts/Location"
import { motion } from "framer-motion"
import { LanguageContext } from "../../../../contexts/Language"
import { Link } from "../../../atoms/Link"
import useTealiumEvent from "../../../../hooks/Tealium/useTealiumEvent"
import { useTealiumContext } from "../../../../contexts/Tealium"
import useVehicleData from "../../../../hooks/Tealium/useVehicleData"

const LeaseFeaturedMobileLinks: React.FC<LeaseFeaturedMobileLinksProps> = ({
  seriesData,
  asShownMsrp,
  featuredModel,
  featuredOffer,
  seriesSlug,
  inventoryCount,
  slideIndex,
  extraTealiumValues,
}) => {
  // Dealers/inventory CTAs logic
  const [{ city, dealers }] = useContext(LocationContext)
  const { _, language } = useContext(LanguageContext)

  // Tealium
  const { trackTealEvent } = useTealiumEvent()
  const { tealPageData } = useTealiumContext()
  const [vehicleData] = useVehicleData()

  return (
    <>
      <motion.section
        animate={{ opacity: 1 }}
        initial={{ opacity: 0 }}
        transition={{ duration: 1, delay: 0.2, type: "spring" }}
        css={[
          tw`col-start-1 col-end-3 row-start-5 row-end-6 flex flex-col justify-self-end content-center items-center z-10 bg-black bg-opacity-100 px-20 w-full pt-32`,
          tw`lg:(pb-16 justify-self-end)`,
          tw`xl:(z-20 col-start-2 col-end-3 row-start-6 row-end-7 p-6 pb-6 mr-40  w-auto bg-black bg-opacity-80 backdrop-blur-[1px] rounded-t-lg)`,
          tw`2xl:(ml-96 justify-self-start)`,
          tw`desktop-hd:(ml-96 justify-self-start)`,
        ]}
        className="optimizelyTest10DesignControl optimizelyOfferHeroLinks"
      >
        <>
          <PriceAndMPG
            css={[
              tw`flex-row pt-16 md:pt-24 lg:pt-20 xl:pt-0 `, // Over-ride default component styles
            ]}
            msrp={seriesData?.series?.msrp}
            asShownMsrp={asShownMsrp}
            mpgCity={featuredModel?.mpgCity}
            mpgHighway={featuredModel?.mpgHighway}
            range={featuredModel?.range}
            light
            small
            seriesName={featuredOffer?.series?.name}
            seriesYear={featuredOffer?.year?.toString()}
          />
          <div css={[tw`text-center text-xs italic text-gray-500`]}>
            {featuredOffer?.series?.currentYear} {featuredOffer?.series?.name}{" "}
            {_("shown with options.")}
          </div>
          {/* CTAs */}
          <section
            css={[
              tw`col-start-1 col-end-3 row-start-6 row-end-7 flex flex-wrap justify-center gap-3 pt-12 bg-black`,
              tw`lg:py-5`,
              tw`xl:hidden`,
            ]}
          >
            {/* TODO: Change secondary button to "Details" as per mockup. Offers template not currently up, so will come back to this when it is. */}
            <LocalInventoryLinks
              city={city}
              dealerCount={dealers?.length}
              inventoryLink={seriesSlug}
              inventoryCount={inventoryCount}
              hideLocation
              series={featuredOffer?.series?.name}
              year={featuredOffer?.series?.currentYear}
              css={[
                `& > div {
              padding: 0;
            }`,
              ]}
              analyticsId={`offers:${slideIndex + 1}`}
              extraTealiumValues={extraTealiumValues}
              fromOffers={true}
            />
          </section>
        </>
      </motion.section>
      <motion.section
        animate={{ opacity: 1 }}
        initial={{ opacity: 0 }}
        transition={{ duration: 1, delay: 0.2, type: "spring" }}
        css={[
          tw`col-start-1 col-end-3 row-start-5 row-end-6 flex flex-col justify-self-end content-center items-center bg-black bg-opacity-100 px-20 w-full pt-32 hidden`,
          tw`lg:(pb-16 pt-24 justify-self-end)`,
          tw`xl:(z-20 col-start-2 col-end-3 row-start-6 row-end-7 p-6 pb-6 mr-40  w-auto bg-black bg-opacity-80 backdrop-blur-[1px] rounded-t-lg)`,
          tw`2xl:(ml-96 justify-self-start)`,
          tw`desktop-hd:(ml-96 justify-self-start)`,
        ]}
        className="optimizelyTest10DesignVariant optimizelyOfferHeroLinks"
      >
        <>
          <PriceAndMPG
            css={[
              tw`flex-row pt-8 md:pt-24 lg:pt-20 xl:pt-0 `, // Over-ride default component styles
            ]}
            msrp={seriesData?.series?.msrp}
            asShownMsrp={asShownMsrp}
            mpgCity={featuredModel?.mpgCity}
            mpgHighway={featuredModel?.mpgHighway}
            range={featuredModel?.range}
            light
            small
            seriesName={featuredOffer?.series?.name}
            seriesYear={featuredOffer?.year?.toString()}
          />
          <div css={[tw`text-center text-xs italic text-gray-500`]}>
            {featuredOffer?.series?.currentYear} {featuredOffer?.series?.name}{" "}
            {_("shown with options.")}
          </div>
          {/* CTAs */}
          <section
            css={[
              tw`col-start-1 col-end-3 row-start-6 row-end-7 flex flex-col justify-center gap-3 pt-6 bg-black`,
              tw`lg:pt-5`,
              tw`xl:hidden`,
            ]}
          >
            {/* TODO: Change secondary button to "Details" as per mockup. Offers template not currently up, so will come back to this when it is. */}
            <>
              <LocalInventoryLinks
                city={city}
                dealerCount={dealers?.length}
                inventoryLink={seriesSlug}
                inventoryCount={inventoryCount}
                hideLocation
                series={featuredOffer?.series?.name}
                year={featuredOffer?.series?.currentYear}
                css={[
                  `& > div {
            padding: 0;
          }`,
                ]}
                analyticsId={`offers:${slideIndex + 1}`}
                extraTealiumValues={extraTealiumValues}
                fromOffers={true}
              />
              <motion.div
                css={[
                  tw`text-xs text-gray-300 gap-3 z-20 mx-auto uppercase hidden hover:(underline) xl:(hidden) `,
                ]}
                animate={{ opacity: 1 }}
                initial={{ opacity: 0 }}
                transition={{ duration: 1, delay: 0.5, type: "spring" }}
                className="optimizelyTest10DesignVariant optimizelyTest10PrimaryMetric"
              >
                {featuredOffer?.type == "Lease" && (
                  <Link
                    to={
                      language === "es"
                        ? `/es/offers/${featuredOffer.slug}`
                        : `/offers/${featuredOffer.slug}`
                    }
                    target="_self" // Needed for expired offer redirect
                    forceExternal={true}
                    onClick={() => {
                      // Conditionally add values if they are available
                      const tealOptionalValues: {
                        dealer_name?: string
                        dealer_code?: string
                        vehicle_model_truncated?: string
                        vehicle_model?: string
                        vehicle_year?: string | number
                      } = {
                        ...vehicleData,
                      }

                      trackTealEvent({
                        ...tealOptionalValues,
                        offer_type: `${tealPageData.page_type} | ${tealPageData.page_name} | Lease | ${featuredOffer?.name}`,
                        alloy_offer_id: featuredOffer?._id, //  Offer ID not on SDR but used for isFavorite check in useTealiumEvent - Jira ticket: SETMKT-456
                        tealium_event: "view_offer",
                      })
                    }}
                    analytics-id={
                      slideIndex
                        ? `offer hero:offers:${slideIndex + 1}`
                        : "offer hero:offers:"
                    }
                  >
                    {_("View Offer Details")}
                  </Link>
                )}
              </motion.div>
            </>
          </section>
        </>
      </motion.section>
    </>
  )
}

export default LeaseFeaturedMobileLinks
